import React from 'react';
import { useIntl } from 'react-intl';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { SerializedReel } from '../../types';
import { reelReviewMessages } from './reelReviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelRejectedNoticeProps {
  reel: SerializedReel;
  /** The reel made from this one, when the history holds it. */
  childUuid?: string;
  isMaking: boolean;
  onMakeAnother: () => void;
  onOpenChild: (uuid: string) => void;
}

/**
 * A rejected reel (design e4): the notes, then "Make another" when no new reel came from it
 * (`canRegenerate`, owner ruling), or a link to the new reel.
 */
const ReelRejectedNoticeComponent: React.FC<ReelRejectedNoticeProps> = (props) => {
  const { reel, childUuid, isMaking, onMakeAnother, onOpenChild } = props;
  const intl = useIntl();
  const { videoCreditsPerMonth } = useMusic24Capabilities();

  return (
    <>
      {reel.rejectReason !== null && reel.rejectReason !== '' && (
        <div>
          <div className={styles.secHead}>
            <h3 className={styles.secTitle}>{intl.formatMessage(reelReviewMessages.yourNotes)}</h3>
          </div>
          <p className={`${styles.script} ${styles.scriptPlain}`}>{reel.rejectReason}</p>
        </div>
      )}
      <p className={styles.tiny}>
        {intl.formatMessage(reel.canRegenerate ? reelReviewMessages.noChild : reelReviewMessages.hasChild)}
      </p>
      {reel.canRegenerate && (
        <div className={styles.actions}>
          <SMLButton
            color="primary"
            omitCap
            leftIcon={faSparkles}
            isLoading={isMaking}
            disabled={isMaking}
            onClick={onMakeAnother}
          >
            {intl.formatMessage(reelReviewMessages.makeAnother)}
          </SMLButton>
          {/* An unlimited plan (`null`) has no allowance to show, as in the rail. */}
          {videoCreditsPerMonth !== null && (
            <span className={styles.tiny}>
              {intl.formatMessage(reelReviewMessages.makeAnotherHint, { limit: videoCreditsPerMonth })}
            </span>
          )}
        </div>
      )}
      {!reel.canRegenerate && childUuid !== undefined && (
        <div className={styles.actions}>
          <SMLButton outline omitCap onClick={() => onOpenChild(childUuid)}>
            {intl.formatMessage(reelReviewMessages.openChild)}
          </SMLButton>
        </div>
      )}
    </>
  );
};

export const ReelRejectedNotice = TypedMemo(ReelRejectedNoticeComponent);
